import { forwardRef, useCallback, useImperativeHandle, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { PresentUrl, employeementFormApi } from "../../../text/apidata.js";
import { store } from "../../../Redux/Store.js";
import { getLoaderAction2 } from "../../../Redux/Loader2.js";
import {
  selectEmployeeInformation,
  setPreviousEmploymentFresher,
  addPreviousEmployment,
  removePreviousEmployment,
  updatePreviousEmploymentField,
  setPreviousEmploymentImage,
  removePreviousEmploymentImage,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./PreviousEmploymentDetails.module.scss";

const createEmploymentEntry = () => ({
  id: Date.now(),
  company_name: "",
  start_date: "",
  end_date: "",
  years_of_experience: "",
  month_of_experience: "",
  designation: "",
  certificate: "",
  is_fresher: false,
});

const calculateExperience = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { years: "", months: "" };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    end < start
  ) {
    return { years: "", months: "" };
  }

  let totalMonths =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());

  if (end.getDate() < start.getDate()) {
    totalMonths -= 1;
  }

  totalMonths = Math.max(totalMonths, 0);

  return {
    years: String(Math.floor(totalMonths / 12)),
    months: String(totalMonths % 12),
  };
};

const PreviousEmploymentDetails = forwardRef(
  function PreviousEmploymentDetails(_, ref) {
    const dispatch = useDispatch();

    const { previousEmploymentDetails, previousEmploymentImages } = useSelector(
      selectEmployeeInformation
    );

    const selfonboardData = useSelector(
      (state) => state.selfonboardingretriveslice?.Selfonboarding_Data
    );
    const employeeDetails = useSelector(
      (state) => state.Signature_Employee_RetrieveCall_actions?.Signature_Employee_Datas
    );

    const { isFresher, entries } = previousEmploymentDetails;
    const [errorFields, setErrorFields] = useState({});

    const extractEmploymentFormId = (d) => {
      if (!d) return null;
      const raw =
        d?.employeetimeline_id?.employee_previous ||
        d?.employeetimeline_id?.previous_details_form ||
        d?.previous_details_form?.id ||
        d?.previous_details_form ||
        d?.previous_details_id ||
        d?.previous_details ||
        d?.employee_previous;

      if (typeof raw === "object" && raw !== null) {
        return raw.id || raw._id || null;
      }
      return raw ? String(raw) : null;
    };

    const fetchedEmploymentFormRef = useRef(false);

    // GET Previous Employment Form API on mount
    useEffect(() => {
      const details = selfonboardData || employeeDetails;
      if (!details) {
        dispatch(getLoaderAction2(false));
        return;
      }

      const employmentFormId = extractEmploymentFormId(details);
      if (!employmentFormId) {
        dispatch(getLoaderAction2(false));
        return;
      }

      if (fetchedEmploymentFormRef.current === employmentFormId) {
        dispatch(getLoaderAction2(false));
        return;
      }
      fetchedEmploymentFormRef.current = employmentFormId;

      const sessionToken =
        details?.session_token ||
        details?.token ||
        details?.sessionToken ||
        store?.getState()?.users?.UserData?.session?.token;

      const headers = sessionToken
        ? {
          Authorization: String(sessionToken).startsWith("Bearer ")
            ? sessionToken
            : `Bearer ${sessionToken}`,
        }
        : {};

      dispatch(getLoaderAction2(true));

      axios
        .get(`${employeementFormApi}`, { headers })
        .then((res) => {
          console.log("Previous Employment form GET API response:", res.data);
          const raw = res.data?.data || res.data?.results || res.data;
          const dataArr = Array.isArray(raw) ? raw : (raw && typeof raw === "object" ? [raw] : []);

          if (dataArr.length > 0) {
            const first = dataArr[0];
            if (first.is_fresher) {
              dispatch(setPreviousEmploymentFresher(true));
            } else {
              dispatch(setPreviousEmploymentFresher(false));

              dataArr.forEach((item, idx) => {
                const existing = entries[idx];
                const entryId = existing ? existing.id : Date.now() + idx;

                const exp = calculateExperience(item.start_date, item.end_date);
                const yearsExp = item.years_of_experience || exp.years;
                const monthExp = item.month_of_experience || exp.months;

                const certObj =
                  item.certificate_id ||
                  item.certificate ||
                  item.certificate_url ||
                  item.media_url ||
                  item.experience_certificate;

                const certUrl =
                  (typeof certObj === "object" && certObj !== null
                    ? certObj?.media_url || certObj?.url
                    : typeof certObj === "string"
                      ? certObj
                      : "") || "";

                if (existing) {
                  dispatch(updatePreviousEmploymentField({ id: entryId, name: "company_name", value: item.company_name || "" }));
                  dispatch(updatePreviousEmploymentField({ id: entryId, name: "start_date", value: item.start_date || "" }));
                  dispatch(updatePreviousEmploymentField({ id: entryId, name: "end_date", value: item.end_date || "" }));
                  dispatch(updatePreviousEmploymentField({ id: entryId, name: "years_of_experience", value: yearsExp }));
                  dispatch(updatePreviousEmploymentField({ id: entryId, name: "month_of_experience", value: monthExp }));
                  dispatch(updatePreviousEmploymentField({ id: entryId, name: "designation", value: item.designation || "" }));
                  dispatch(updatePreviousEmploymentField({ id: entryId, name: "certificate", value: certUrl }));
                } else {
                  dispatch(
                    addPreviousEmployment({
                      id: entryId,
                      company_name: item.company_name || "",
                      start_date: item.start_date || "",
                      end_date: item.end_date || "",
                      years_of_experience: yearsExp,
                      month_of_experience: monthExp,
                      designation: item.designation || "",
                      certificate: certUrl,
                      is_fresher: false,
                    })
                  );
                }

                if (certUrl) {
                  dispatch(
                    setPreviousEmploymentImage({
                      id: entryId,
                      image: {
                        name: "Experience Certificate",
                        url: certUrl,
                        s3Url: certUrl,
                      },
                    })
                  );
                }
              });
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching Previous Employment GET API:", err);
        })
        .finally(() => {
          dispatch(getLoaderAction2(false));
        });
    }, [dispatch, selfonboardData, employeeDetails, entries]);

    const clearError = (id, name) => {
      setErrorFields((prev) => {
        const entryErrors = new Set(prev[id] || []);
        entryErrors.delete(name);
        return {
          ...prev,
          [id]: entryErrors,
        };
      });
    };

    const hasError = (id, name) => errorFields[id]?.has(name) ?? false;

    const handleAddEmployment = () => {
      dispatch(addPreviousEmployment(createEmploymentEntry()));
    };

    const handleRemoveEmployment = (id) => {
      if (previousEmploymentImages[id]?.url) {
        URL.revokeObjectURL(previousEmploymentImages[id].url);
      }

      dispatch(removePreviousEmployment(id));

      setErrorFields((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    };

    const updateField = (id, name, value) => {
      dispatch(updatePreviousEmploymentField({ id, name, value }));
      clearError(id, name);
    };

    const handleDateChange = (id, startDate, endDate) => {
      const exp = calculateExperience(startDate, endDate);
      updateField(id, "years_of_experience", exp.years);
      updateField(id, "month_of_experience", exp.months);
    };

    const handleFile = async (id, file) => {
      if (!file || file.size > 10 * 1024 * 1024) {
        return;
      }

      if (previousEmploymentImages[id]?.url) {
        URL.revokeObjectURL(previousEmploymentImages[id].url);
      }

      const localUrl = URL.createObjectURL(file);

      dispatch(
        setPreviousEmploymentImage({
          id,
          image: {
            name: file.name,
            file,
            url: localUrl,
            s3Url: "",
          },
        })
      );

      const details = selfonboardData || employeeDetails;
      const sessionToken =
        details?.session_token ||
        details?.token ||
        details?.sessionToken ||
        store?.getState()?.users?.UserData?.session?.token;

      const headers = sessionToken
        ? {
          Authorization: String(sessionToken).startsWith("Bearer ")
            ? sessionToken
            : `Bearer ${sessionToken}`,
        }
        : {};

      dispatch(getLoaderAction2(true));

      try {
        const folderName = "experience";
        const randomNumber = Math.floor(Math.random() * 10000000000);
        const fileExt = file.name.split(".").pop() || "jpg";
        const formattedFilename = `onboarding/${folderName}/${folderName}${randomNumber}.${fileExt}`;

        const presignedReqData = {
          multiple_files: [
            {
              filename: formattedFilename,
              file_type: file.type || "image/jpeg",
            },
          ],
        };

        const presignedRes = await axios.post(PresentUrl, presignedReqData, { headers });
        const uploadedUrls = presignedRes.data?.data || presignedRes.data;

        let s3CleanUrl = "";
        if (Array.isArray(uploadedUrls) && uploadedUrls.length > 0) {
          const presignedPutUrl = uploadedUrls[0];
          s3CleanUrl = String(presignedPutUrl).split("?")[0];

          await fetch(presignedPutUrl, {
            method: "PUT",
            headers: {
              "Content-Type": file.type || "image/jpeg",
              "x-amz-acl": "public-read",
            },
            body: file,
          });
        }

        const finalS3Url = s3CleanUrl || localUrl;

        dispatch(
          setPreviousEmploymentImage({
            id,
            image: {
              name: file.name,
              file,
              url: finalS3Url,
              s3Url: finalS3Url,
            },
          })
        );
        updateField(id, "certificate", finalS3Url);
      } catch (err) {
        console.error("Presigned URL upload error for experience certificate:", err);
      } finally {
        dispatch(getLoaderAction2(false));
      }
    };

    const getMissingFields = useCallback(() => {
      if (isFresher) {
        return [];
      }

      if (entries.length === 0) {
        return ["Previous Employment"];
      }

      const missing = [];
      const newErrorFields = {};

      entries.forEach((entry, index) => {
        const label = `Employment ${index + 1}`;
        const entryErrors = new Set();

        if (!String(entry.company_name || "").trim()) {
          missing.push(`${label} Company Name`);
          entryErrors.add("company_name");
        }

        if (!entry.start_date) {
          missing.push(`${label} Start Date`);
          entryErrors.add("start_date");
        }

        if (!entry.end_date) {
          missing.push(`${label} End Date`);
          entryErrors.add("end_date");
        }

        if (!String(entry.designation || "").trim()) {
          missing.push(`${label} Designation`);
          entryErrors.add("designation");
        }

        if (entry.start_date && entry.end_date) {
          const start = new Date(entry.start_date);
          const end = new Date(entry.end_date);

          if (end < start) {
            missing.push(`${label} valid date range`);
            entryErrors.add("start_date");
            entryErrors.add("end_date");
          }
        }

        newErrorFields[entry.id] = entryErrors;
      });

      setErrorFields(newErrorFields);

      return missing;
    }, [entries, isFresher]);

    useImperativeHandle(ref, () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }));

    return (
      <div className={styles.previousEmploymentDetails}>
        <header className={styles.sectionHeader}>
          Previous Employee Details
        </header>

        <div className={styles.content}>
          <label className={styles.fresherCheck}>
            <input
              type="checkbox"
              checked={isFresher}
              onChange={(event) => {
                const checked = event.target.checked;

                dispatch(setPreviousEmploymentFresher(checked));

                if (!checked && entries.length === 0) {
                  dispatch(addPreviousEmployment(createEmploymentEntry()));
                }
              }}
            />
            <span>Employee is fresher ?</span>
          </label>

          {!isFresher ? (
            <button
              type="button"
              className={styles.addButton}
              onClick={handleAddEmployment}
              aria-label="Add previous employment"
              title="Add previous employment"
            >
              +
            </button>
          ) : null}

          {!isFresher && entries.length > 0 ? (
            <div className={styles.entries}>
              {entries.map((entry, index) => {
                const experience = calculateExperience(
                  entry.start_date,
                  entry.end_date
                );

                return (
                  <section className={styles.entry} key={entry.id}>
                    <div className={styles.entryHeader}>
                      <h3>Employment {index + 1}</h3>

                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemoveEmployment(entry.id)}
                        aria-label={`Remove employment ${index + 1}`}
                        title="Remove employment"
                      >
                        ×
                      </button>
                    </div>

                    <div className={styles.formGrid}>
                      <label className={styles.field}>
                        <span>
                          Company Name<b>*</b>
                        </span>
                        <input
                          value={entry.company_name || ""}
                          className={
                            hasError(entry.id, "company_name")
                              ? styles.inputError
                              : ""
                          }
                          onChange={(event) =>
                            updateField(
                              entry.id,
                              "company_name",
                              event.target.value.toUpperCase()
                            )
                          }
                        />
                        {hasError(entry.id, "company_name") && (
                          <p className={styles.fieldError}>
                            Please fill this field
                          </p>
                        )}
                      </label>

                      <label className={styles.field}>
                        <span>
                          Start Date<b>*</b>
                        </span>
                        <input
                          type="date"
                          value={entry.start_date || ""}
                          className={
                            hasError(entry.id, "start_date")
                              ? styles.inputError
                              : ""
                          }
                          onChange={(event) => {
                            const val = event.target.value;
                            updateField(entry.id, "start_date", val);
                            handleDateChange(entry.id, val, entry.end_date);
                          }}
                        />
                        {hasError(entry.id, "start_date") && (
                          <p className={styles.fieldError}>
                            Please select a valid start date
                          </p>
                        )}
                      </label>

                      <label className={styles.field}>
                        <span>
                          End Date<b>*</b>
                        </span>
                        <input
                          type="date"
                          value={entry.end_date || ""}
                          className={
                            hasError(entry.id, "end_date")
                              ? styles.inputError
                              : ""
                          }
                          onChange={(event) => {
                            const val = event.target.value;
                            updateField(entry.id, "end_date", val);
                            handleDateChange(entry.id, entry.start_date, val);
                          }}
                        />
                        {hasError(entry.id, "end_date") && (
                          <p className={styles.fieldError}>
                            Please select a valid end date
                          </p>
                        )}
                      </label>

                      <label className={styles.field}>
                        <span>
                          Year Of Experience<b>*</b>
                        </span>
                        <input
                          readOnly
                          value={entry.years_of_experience || experience.years}
                        />
                      </label>

                      <label className={styles.field}>
                        <span>
                          Designation<b>*</b>
                        </span>
                        <input
                          value={entry.designation || ""}
                          className={
                            hasError(entry.id, "designation")
                              ? styles.inputError
                              : ""
                          }
                          onChange={(event) =>
                            updateField(
                              entry.id,
                              "designation",
                              event.target.value.toUpperCase()
                            )
                          }
                        />
                        {hasError(entry.id, "designation") && (
                          <p className={styles.fieldError}>
                            Please fill this field
                          </p>
                        )}
                      </label>

                      <label className={styles.field}>
                        <span>Month Of Experience</span>
                        <input
                          readOnly
                          value={entry.month_of_experience || experience.months}
                        />
                      </label>
                    </div>

                    <div className={styles.uploadBlock}>
                      <span>File Upload</span>

                      <p className={styles.note}>
                        Note: Upload the picture in JPG, JPEG, or PNG format.
                        Maximum size: 10 MB.
                      </p>

                      {(() => {
                        const imgObj = previousEmploymentImages[entry.id] || entry.certificate;
                        let certSrc = "";
                        if (typeof imgObj === "string") {
                          certSrc = imgObj;
                        } else if (typeof imgObj === "object" && imgObj !== null) {
                          if (typeof imgObj.url === "object" && imgObj.url !== null) {
                            certSrc = imgObj.url.media_url || imgObj.url.url || "";
                          } else {
                            certSrc = imgObj.url || imgObj.media_url || imgObj.s3Url || "";
                          }
                        }

                        if (!certSrc) return null;

                        const isImg = /\.(jpg|jpeg|png|webp|gif)/i.test(certSrc) || certSrc.includes("amazonaws.com");

                        return (
                          <div className={styles.previewRow}>
                            {isImg ? (
                              <img
                                src={certSrc}
                                alt="Experience Certificate preview"
                                className={styles.imagePreview}
                                style={{ maxHeight: "80px", borderRadius: "8px", objectFit: "cover" }}
                              />
                            ) : (
                              <a href={certSrc} target="_blank" rel="noreferrer">
                                View Uploaded File
                              </a>
                            )}
                            <button
                              type="button"
                              className={styles.fileRemoveButton}
                              onClick={() => {
                                dispatch(removePreviousEmploymentImage(entry.id));
                                updateField(entry.id, "certificate", "");
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })() || (
                        <label className={styles.uploadButton}>
                          Upload Image
                          <input
                            type="file"
                            className={styles.hiddenInput}
                            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                            onChange={(event) =>
                              handleFile(entry.id, event.target.files?.[0])
                            }
                          />
                        </label>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);

export default PreviousEmploymentDetails;
